import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { ppaUrls } from 'config/urls';
import { FilterContextData } from 'contexts/FilterContext';
import useWhitespaceAnalysisGridData, { WhitespaceGridRow } from 'hooks/useWhitespaceAnalysisGridData';
import React, { FC, createContext, useContext, useEffect, useMemo, useState } from 'react';

type WhitespaceOptionItem = {
    id_type?: string;
    id?: string;
    label?: string;
    dimension?: string | null;
    is_attribute?: boolean | null;
    is_default?: boolean;
    is_mandatory?: boolean;
    input_list?: Array<{
        input_id?: string;
        input_type?: string;
        default_value?: number;
        increment_rate?: number;
        bounds?: {
            min_val?: number;
            max_val?: number;
        };
    }>;
};

type WhitespaceDropdownConfig = {
    id?: string;
    group_by_options?: WhitespaceOptionItem[];
    column_options?: WhitespaceOptionItem[];
};

type WhitespaceMetaResponse = {
    title?: string;
    tooltip?: string;
    options?: {
        drop_down?: WhitespaceDropdownConfig[];
        dropdowns?: WhitespaceDropdownConfig[];
        slider?: Array<{
            id?: string;
            label?: string;
            default_value?: number;
            min_value?: number;
            max_value?: number;
            step?: number;
            input_id?: string;
        }>;
        toggle?: Array<{
            id?: string;
            label?: string;
            default_enabled?: boolean;
            input_list?: Array<{
                input_id?: string;
                default_value?: boolean;
                placeholder?: string;
            }>;
        }>;
    };
    page_limit?: number;
    child_limit?: number;
    column_limit?: number;
    row_limit?: number;
};

type WhitespaceAnalysisContextValue = {
    title: string;
    tooltip: string;
    xAxis: string;
    setXAxis: React.Dispatch<React.SetStateAction<string>>;
    yAxis: string;
    setYAxis: React.Dispatch<React.SetStateAction<string>>;
    selectedYAxisKpiId: string;
    crossSection: string;
    setCrossSection: React.Dispatch<React.SetStateAction<string>>;
    incrementValue: number;
    setIncrementValue: React.Dispatch<React.SetStateAction<number>>;
    incrementConfig: {
        label: string;
        min: number;
        max: number;
        step: number;
        defaultValue: number;
    };
    showWhitespaces: boolean;
    setShowWhitespaces: React.Dispatch<React.SetStateAction<boolean>>;
    xAxisOptions: string[];
    yAxisOptions: string[];
    crossSectionOptions: string[];
    xAxisFilter: {
        dimension: string;
        attributeType: string;
        isAttribute: boolean;
    };
    childLimit: number;
    gridRequestPayload: Record<string, unknown>;
    columnHeaders: string[];
    gridRows: WhitespaceGridRow[];
    loadMoreRows: () => Promise<void>;
    loadMoreColumns: () => Promise<void>;
    hasMoreRows: boolean;
    hasMoreColumns: boolean;
    isFetchingMoreRows: boolean;
    isFetchingMoreColumns: boolean;
    isMetaLoading: boolean;
    isGridLoading: boolean;
    isGridFetching: boolean;
    hasGridError: boolean;
    isGridFetched: boolean;
};

const EMPTY_CONTEXT: WhitespaceAnalysisContextValue = {
    title: '',
    tooltip: '',
    xAxis: '',
    setXAxis: () => undefined,
    yAxis: '',
    setYAxis: () => undefined,
    selectedYAxisKpiId: '',
    crossSection: '',
    setCrossSection: () => undefined,
    incrementValue: 1,
    setIncrementValue: () => undefined,
    incrementConfig: {
        label: '',
        min: 0,
        max: 0,
        step: 0,
        defaultValue: 0,
    },
    showWhitespaces: false,
    setShowWhitespaces: () => undefined,
    xAxisOptions: [],
    yAxisOptions: [],
    crossSectionOptions: [],
    xAxisFilter: {
        dimension: 'dim1',
        attributeType: '',
        isAttribute: false,
    },
    childLimit: 0,
    gridRequestPayload: {},
    columnHeaders: [],
    gridRows: [],
    loadMoreRows: async () => undefined,
    loadMoreColumns: async () => undefined,
    hasMoreRows: false,
    hasMoreColumns: false,
    isFetchingMoreRows: false,
    isFetchingMoreColumns: false,
    isMetaLoading: true,
    isGridLoading: false,
    isGridFetching: false,
    hasGridError: false,
    isGridFetched: false,
};

const WhitespaceAnalysisDataContext = createContext<WhitespaceAnalysisContextValue>(EMPTY_CONTEXT);

type Props = {
    children: React.ReactNode;
};

const EMPTY_DROPDOWNS: WhitespaceDropdownConfig[] = [];
const EMPTY_OPTION_ITEMS: WhitespaceOptionItem[] = [];

const getOptionLabel = (option: WhitespaceOptionItem) => option.label ?? option.id ?? '';

const getDefaultOptionLabel = (options: WhitespaceOptionItem[]) =>
    getOptionLabel(options.find((option) => option.is_default) ?? options[0] ?? {});

const toNumeric = (value: unknown, fallback = 0): number => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    }
    return fallback;
};

const WhitespaceAnalysisContext = ({ children }: Props) => {
    const { timePeriodSelection, idForRestOfComponent, idForRestOfComponentLoading } = useContext(FilterContextData);
    const [xAxis, setXAxis] = useState('');
    const [yAxis, setYAxis] = useState('');
    const [crossSection, setCrossSection] = useState('');
    const [incrementValue, setIncrementValue] = useState(1);
    const [showWhitespaces, setShowWhitespaces] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    const { data: meta, isLoading: isMetaLoading } = useQuery<WhitespaceMetaResponse>({
        queryKey: ['whitespace-analysis-meta'],
        queryFn: async () => {
            const response = await axios.get(ppaUrls.getWhitespaceAnalysisMetaUrl);
            return response.data;
        },
    });
    const title = meta?.title ?? '';
    const tooltip = meta?.tooltip ?? '';

    const dropdowns = useMemo(
        () => meta?.options?.drop_down ?? meta?.options?.dropdowns ?? EMPTY_DROPDOWNS,
        [meta?.options?.drop_down, meta?.options?.dropdowns],
    );
    const xAxisDropdown = useMemo(() => dropdowns.find((dropdown) => dropdown.id === 'x_axis'), [dropdowns]);
    const yAxisDropdown = useMemo(() => dropdowns.find((dropdown) => dropdown.id === 'y_axis'), [dropdowns]);
    const crossSectionDropdown = useMemo(() => dropdowns.find((dropdown) => dropdown.id === 'z_axis'), [dropdowns]);
    const xAxisOptionItems = useMemo(
        () => xAxisDropdown?.group_by_options ?? EMPTY_OPTION_ITEMS,
        [xAxisDropdown?.group_by_options],
    );
    const yAxisOptionItems = useMemo(
        () => yAxisDropdown?.column_options ?? EMPTY_OPTION_ITEMS,
        [yAxisDropdown?.column_options],
    );
    const crossSectionOptionItems = useMemo(
        () => crossSectionDropdown?.column_options ?? EMPTY_OPTION_ITEMS,
        [crossSectionDropdown?.column_options],
    );

    const xAxisOptions = useMemo(() => xAxisOptionItems.map(getOptionLabel).filter(Boolean), [xAxisOptionItems]);
    const yAxisOptions = useMemo(() => yAxisOptionItems.map(getOptionLabel).filter(Boolean), [yAxisOptionItems]);
    const crossSectionOptions = useMemo(
        () => crossSectionOptionItems.map(getOptionLabel).filter(Boolean),
        [crossSectionOptionItems],
    );

    const selectedYAxisOption = yAxisOptionItems.find((option) => getOptionLabel(option) === yAxis);
    const defaultYAxisOption = yAxisOptionItems.find((option) => option.is_default) ?? yAxisOptionItems[0];
    const effectiveYAxisOption = selectedYAxisOption ?? defaultYAxisOption;
    const selectedXAxisOption = xAxisOptionItems.find((option) => getOptionLabel(option) === xAxis);
    const selectedCrossSectionOption = crossSectionOptionItems.find((option) => getOptionLabel(option) === crossSection);

    const sliderConfig = meta?.options?.drop_down?.find((option) => option.id === 'y_axis') || {};
    const selectedYAxisSlider = useMemo(() => {
        const result = sliderConfig?.column_options?.find((option) => option.id === effectiveYAxisOption?.id);
        return result?.input_list?.[0];
    }, [effectiveYAxisOption, sliderConfig?.column_options]);

    const incrementConfig = {
        label: 'Increments:',
        min: toNumeric(selectedYAxisSlider?.bounds?.min_val) || 0,
        max: toNumeric(selectedYAxisSlider?.bounds?.max_val) || 100,
        step: toNumeric(selectedYAxisSlider?.increment_rate) || 1,
        defaultValue: toNumeric(selectedYAxisSlider?.default_value) || 0,
    };

    const toggleConfig = meta?.options?.toggle?.[0];
    const toggleInput = toggleConfig?.input_list?.[0];
    const whitespaceToggleDefault = toggleConfig?.default_enabled ?? toggleInput?.default_value ?? false;

    useEffect(() => {
        if (!meta || isInitialized) return;
        setXAxis(getDefaultOptionLabel(xAxisOptionItems));
        setYAxis(getDefaultOptionLabel(yAxisOptionItems));
        setCrossSection(getDefaultOptionLabel(crossSectionOptionItems));
        setIncrementValue(incrementConfig.defaultValue);
        setShowWhitespaces(Boolean(whitespaceToggleDefault));
        setIsInitialized(true);
    }, [
        crossSectionOptionItems,
        incrementConfig.defaultValue,
        isInitialized,
        meta,
        whitespaceToggleDefault,
        xAxisOptionItems,
        yAxisOptionItems,
    ]);

    const parsedTimePeriod = useMemo(() => JSON.parse(timePeriodSelection ?? '{}'), [timePeriodSelection]);
    const startTimestamp = parsedTimePeriod?.start_timestamp;
    const endTimestamp = parsedTimePeriod?.end_timestamp;

    const gridRequestPayload = useMemo(() => {
        const selectedXAxisDimension = selectedXAxisOption?.dimension ?? 'dim1';
        const selectedCrossSectionDimension = selectedCrossSectionOption?.dimension ?? selectedXAxisDimension;

        const selectedYAxisKpiId = effectiveYAxisOption?.id ?? 'value_sales';

        return {
            kpi_ids: [selectedYAxisKpiId],
            time_period: {
                start_timestamp: startTimestamp,
                end_timestamp: endTimestamp,
            },
            filters: {
                dim1_id_list: idForRestOfComponent?.dim1?.ids ?? [],
                dim2_id_list: idForRestOfComponent?.dim2?.ids ?? [],
            },
            aggregates: [
                {
                    dimension: selectedXAxisDimension,
                    attribute_type: selectedXAxisOption?.id ?? xAxis,
                    is_attribute: Boolean(selectedXAxisOption?.is_attribute),
                    is_cross_section: false,
                },
                {
                    dimension: selectedCrossSectionDimension,
                    attribute_type: selectedCrossSectionOption?.id ?? crossSection,
                    is_attribute: Boolean(selectedCrossSectionOption?.is_attribute),
                    is_cross_section: true,
                },
            ],
            user_inputs: [
                { input_id: 'price_per_unit_user_input', input_val: incrementValue }
            ],
            pagination: {
                child: {
                    limit: toNumeric(meta?.child_limit, 10),
                    offset: 0,
                },
                column: {
                    limit: toNumeric(meta?.column_limit, toNumeric(meta?.page_limit, 10)),
                    offset: 0,
                },
                row: {
                    limit: toNumeric(meta?.row_limit, toNumeric(meta?.page_limit, 10)),
                    offset: 0,
                },
            },
        };
    }, [
        crossSection,
        effectiveYAxisOption?.id,
        endTimestamp,
        idForRestOfComponent?.dim1?.ids,
        idForRestOfComponent?.dim2?.ids,
        incrementValue,
        meta?.child_limit,
        meta?.column_limit,
        meta?.page_limit,
        meta?.row_limit,
        selectedCrossSectionOption?.dimension,
        selectedCrossSectionOption?.id,
        selectedCrossSectionOption?.is_attribute,
        selectedXAxisOption?.dimension,
        selectedXAxisOption?.id,
        selectedXAxisOption?.is_attribute,
        startTimestamp,
        xAxis,
    ]);

    const {
        columnHeaders,
        gridRows,
        isGridLoading,
        isGridFetching,
        hasGridError,
        isGridFetched,
        loadMoreRows,
        loadMoreColumns,
        hasMoreRows,
        hasMoreColumns,
        isFetchingMoreRows,
        isFetchingMoreColumns,
    } = useWhitespaceAnalysisGridData({
        gridRequestPayload,
        yAxis,
        xAxis,
        crossSection,
        startTimestamp,
        endTimestamp,
        idForRestOfComponentLoading,
    });

    return (
        <WhitespaceAnalysisDataContext.Provider
            value={{
                title,
                tooltip,
                xAxis,
                setXAxis,
                yAxis,
                setYAxis,
                selectedYAxisKpiId: effectiveYAxisOption?.id ?? 'value_sales',
                crossSection,
                setCrossSection,
                incrementValue,
                setIncrementValue,
                incrementConfig: {
                    label: incrementConfig.label,
                    min: incrementConfig.min,
                    max: incrementConfig.max,
                    step: incrementConfig.step,
                    defaultValue: incrementConfig.defaultValue,
                },
                showWhitespaces,
                setShowWhitespaces,
                xAxisOptions,
                yAxisOptions,
                crossSectionOptions,
                xAxisFilter: {
                    dimension: selectedXAxisOption?.dimension ?? 'dim1',
                    attributeType: selectedXAxisOption?.id ?? xAxis,
                    isAttribute: Boolean(selectedXAxisOption?.is_attribute),
                },
                childLimit: toNumeric(meta?.child_limit),
                gridRequestPayload,
                columnHeaders,
                gridRows,
                loadMoreRows,
                loadMoreColumns,
                hasMoreRows,
                hasMoreColumns,
                isFetchingMoreRows,
                isFetchingMoreColumns,
                isMetaLoading,
                isGridLoading,
                isGridFetching,
                hasGridError,
                isGridFetched,
            }}
        >
            {children}
        </WhitespaceAnalysisDataContext.Provider>
    );
};

const useWhitespaceAnalysisContext = () => useContext(WhitespaceAnalysisDataContext);

const WhitespaceAnalysisContextWrapper: FC<Props> = ({ children }) => {
    return <WhitespaceAnalysisContext>{children}</WhitespaceAnalysisContext>;
};

export { WhitespaceAnalysisContext, WhitespaceAnalysisContextWrapper, useWhitespaceAnalysisContext };
